'use client';

import { langHome, langPrefix } from '@/lib/locale-path';
import React, { useState } from 'react';
import { Lock, Eye, EyeOff, ShieldCheck, ArrowRight, Loader2, AlertCircle } from 'lucide-react';

// What the server said when it did not grant access.
const UNLOCK_ERRORS: Record<string, { en: string; fr: string }> = {
  invalid_password: {
    en: 'Invalid access password. Please verify and try again.',
    fr: 'Mot de passe d\'accès incorrect. Veuillez réessayer.',
  },
  too_many_attempts: {
    en: 'Too many attempts. Please try again in an hour.',
    fr: 'Trop de tentatives. Veuillez réessayer dans une heure.',
  },
  not_configured: {
    en: 'Access cannot be granted yet: no access code is set. Please contact the site administrator.',
    fr: 'L\'accès ne peut pas encore être accordé : aucun code d\'accès n\'est défini. Veuillez contacter l\'administrateur du site.',
  },
  server_error: {
    en: 'Something went wrong on our side. Please try again.',
    fr: 'Un problème est survenu de notre côté. Veuillez réessayer.',
  },
};

interface MaintenanceClientProps {
  lang: string;
  returnUrl?: string;
}

export default function MaintenanceClient({ lang, returnUrl = '' }: MaintenanceClientProps) {
  const isFr = lang === 'fr';

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // No redirect on load from browser storage: the server decides access (the
  // middleware already sends an unlocked visitor past this page). A stored
  // flag the server did not accept sent the browser away and straight back
  // here in an endless loop, and the form flickered.

  // The server has set its cookie; go where the visitor was heading.
  const executeRedirect = () => {
    try {
      // Left behind by an older version of this page; nothing reads it now.
      localStorage.removeItem('site_access_granted');
      sessionStorage.removeItem('site_access_granted');
    } catch {
      // ignore
    }
    const cleanDest = returnUrl && !returnUrl.includes('/maintenance') ? returnUrl : `${langHome(lang)}`;
    window.location.replace(cleanDest);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || loading) return;

    // The code is set in the admin (Site gate) and checked only on the server.
    setError('');
    setLoading(true);

    // Only the server grants access (it sets the cookie the middleware checks).
    // Leaving the page when it had refused sent the visitor straight back here
    // with no word of why - the password "worked" and nothing happened.
    //
    // A plain request to /api/unlock-site, not a server action: an action's id
    // changes with every update of the site, so a tab opened before one failed
    // with "Server action not found"; and the middleware redirects an action
    // posted to this page once the visitor is already let in. Both showed
    // "Something went wrong". This address never changes and is never
    // redirected.
    let result: { success: boolean; error?: string };
    try {
      const res = await fetch('/api/unlock-site', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
        credentials: 'same-origin',
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body?.success) result = { success: true };
      else if (res.status === 401 || res.status === 400) result = { success: false, error: 'invalid_password' };
      else if (res.status === 429) result = { success: false, error: 'too_many_attempts' };
      else if (res.status === 503 && body?.error === 'not_configured') result = { success: false, error: 'not_configured' };
      else result = { success: false, error: 'server_error' };
    } catch {
      result = { success: false, error: 'server_error' };
    }

    if (!result.success) {
      setLoading(false);
      setError(UNLOCK_ERRORS[result.error || 'server_error']?.[isFr ? 'fr' : 'en'] ?? UNLOCK_ERRORS.server_error[isFr ? 'fr' : 'en']);
      return;
    }

    setSuccess(true);
    executeRedirect();
  };

  const handleLanguageSwitch = (targetLang: 'en' | 'fr') => {
    if (targetLang === lang) return;
    const url = new URL(window.location.href);
    url.pathname = `${langPrefix(targetLang)}/maintenance`;
    window.location.href = url.toString();
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex flex-col justify-between selection:bg-gold selection:text-neutral-950 relative overflow-hidden">
      {/* Subtle architectural background gradients */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,color-mix(in_oklab,var(--color-gold)_8%,transparent),transparent_50%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(30,30,30,0.4),transparent_50%)] pointer-events-none" />

      {/* Top Header Bar */}
      <header className="relative z-10 w-full max-w-6xl mx-auto px-6 py-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-control bg-neutral-900 border border-neutral-800 flex items-center justify-center text-gold shadow-card">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <span className="font-serif tracking-wide text-base font-bold block text-neutral-100">
              Real Estate Channel
            </span>
            <span className="text-xs text-neutral-400 uppercase tracking-widest block font-mono">
              Canada
            </span>
          </div>
        </div>

        {/* Language selector */}
        <div className="flex items-center bg-neutral-900 border border-neutral-800 rounded-control p-1 text-xs font-semibold">
          <button
            type="button"
            onClick={() => handleLanguageSwitch('en')}
            className={`px-3 py-1.5 rounded-small transition-colors cursor-pointer ${
              lang === 'en'
                ? 'bg-neutral-800 text-white shadow-card'
                : 'text-neutral-400 hover:text-white'
            }`}
          >
            English
          </button>
          <button
            type="button"
            onClick={() => handleLanguageSwitch('fr')}
            className={`px-3 py-1.5 rounded-small transition-colors cursor-pointer ${
              lang === 'fr'
                ? 'bg-neutral-800 text-white shadow-card'
                : 'text-neutral-400 hover:text-white'
            }`}
          >
            Français
          </button>
        </div>
      </header>

      {/* Main Content Box */}
      <main id="main-content" className="relative z-10 flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md bg-neutral-900/90 border border-neutral-800 rounded-frame p-8 sm:p-10 shadow-overlay backdrop-blur-md">
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-neutral-800/80 border border-neutral-700 text-neutral-300">
              <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
              <span>
                {isFr ? 'Site en cours de développement' : 'Platform Under Construction'}
              </span>
            </span>
          </div>

          {/* Heading */}
          <div className="text-center space-y-3 mb-8">
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-100">
              {isFr ? 'Lancement Prochain' : 'Launching Soon'}
            </h1>
            <p className="text-xs sm:text-sm text-neutral-400 leading-relaxed max-w-sm mx-auto">
              {isFr
                ? 'Notre plateforme immobilière fait l\'objet de préparatifs finaux. L\'accès est réservé aux personnes autorisées par mot de passe.'
                : 'Our real estate marketplace is currently in private pre-launch preparation. Access is restricted to authorized visitors.'}
            </p>
          </div>

          {/* Content: Either Loading Transition or Access Form */}
          {success ? (
            <div className="py-6 flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-14 h-14 rounded-card bg-success/15 border border-success/50 flex items-center justify-center text-success shadow-overlay animate-pulse">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <div className="space-y-1">
                <h2 className="text-xl font-bold text-white">
                  {isFr ? 'Accès déverrouillé' : 'Access Granted'}
                </h2>
                <p className="text-xs sm:text-sm text-neutral-400">
                  {isFr ? 'Ouverture du site en cours...' : 'Opening platform, please wait...'}
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs text-gold pt-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{isFr ? 'Chargement en cours...' : 'Redirecting now...'}</span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="access-password-input"
                  className="block text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2"
                >
                  {isFr ? 'Mot de passe d\'accès' : 'Access Password'}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    id="access-password-input"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoFocus
                    disabled={loading}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder={isFr ? 'Saisir le mot de passe' : 'Enter access password'}
                    className="field pl-11 pr-11 tracking-wide disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 p-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-300 transition-colors cursor-pointer"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-4 bg-danger/10 border border-danger/30 rounded-control text-xs text-danger flex items-start gap-3">
                  <AlertCircle className="w-4 h-4 text-danger shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !password}
                className="w-full py-4 bg-gold hover:bg-gold/85 text-neutral-950 font-bold rounded-control transition disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer shadow-raised mt-2 text-sm"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <span>{isFr ? 'Accéder au site' : 'Enter Platform'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Discreet status note */}
          <div className="mt-8 pt-6 border-t border-neutral-800/80 text-center">
            <span className="text-xs text-neutral-400 leading-relaxed block">
              {isFr
                ? 'Protection d\'accès temporaire pour le déploiement sur domaine public'
                : 'Temporary pre-launch gate for production domain deployment'}
            </span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 w-full max-w-6xl mx-auto px-6 py-6 text-center text-xs text-neutral-400">
        <span>
          &copy; {new Date().getFullYear()} Real Estate Channel Canada. {isFr ? 'Tous droits réservés.' : 'All rights reserved.'}
        </span>
      </footer>
    </div>
  );
}
